import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import apiClient from '../lib/api/apiClient';

interface User {
  auth_id: string;
  first_name: string;
  last_name: string;
  username: string;
  id: number;
  email: string;
  instagram: string;
  x: string;
  facebook: string;
  avatar: string;
  phone: string;
  role?: string;
  terms_accepted: boolean;
  terms_date: string | null;
  show_name: boolean;
  display_name: string;
  sms_consent?: boolean;
  systemSettings?: {
    [key: string]: string;
  }; // System settings as key-value pairs
  user_data?: {
    myTeam?: number;
    [key: string]: unknown;
  }; // Add user_data field for JSON data with typed myTeam
}

interface UserState {
  user: User | null;
  isAuthenticated: boolean;
  setUser: (user: User | null) => void;
  clearUser: () => void;
  updateUser: (updates: Partial<User>) => Promise<void>;
  setMyTeam: (teamId: number) => Promise<void>; // Add function to set myTeam
}

export const useUserStore = create<UserState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      setUser: (user) => set({ user, isAuthenticated: !!user }),
      clearUser: () => set({ user: null, isAuthenticated: false }),
      setMyTeam: async (teamId: number) => {
        try {
          // Update local state immediately
          set((state: UserState) => ({
            user: state.user ? { 
              ...state.user, 
              user_data: { 
                ...(state.user.user_data || {}), // Handle null case
                myTeam: teamId 
              } 
            } : null
          }));

          // Also update the user profile via API
          const currentUser = useUserStore.getState().user;
          if (currentUser) {
            const updatedUserData = {
              ...(currentUser.user_data || {}), // Handle null case
              myTeam: teamId
            };
            
            await apiClient.put('user/profile', {
              user_data: updatedUserData
            });
          }
        } catch (error) {
          console.error('Failed to update user profile with myTeam:', error);
          // Optionally revert local state on error
        }
      },
      updateUser: async (updates) => {
        try {
          // Make API call to update user data using the new profile endpoint
          const response = await apiClient.put('user/profile', updates);
          
          // Update local state with the full user object returned from the API
          // This ensures we get the correct field names (e.g., 'phone' instead of 'phone_number')
          set((state: UserState) => ({
            user: state.user ? { ...state.user, ...response.data.user } : null
          }));
          
          return response.data;
        } catch (error) {
          console.error('Failed to update user:', error);
          throw error; // Re-throw to let the caller handle the error
        }
      }
    }),
    {
      name: 'user-storage', // unique name for localStorage key
      partialize: (state) => ({ 
        user: state.user, 
        isAuthenticated: state.isAuthenticated 
      }), // only persist user and isAuthenticated, not the functions
    }
  )
);
